import { Check, UserPlus } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";

import { friendlyError } from "../../lib/friendlyError";
import { trpc } from "../../trpc";

import { peopleCount, type EventPrice } from "./guestFormat";
import { formatMoney } from "./shared";

/** How long "Added Priya Nair. Confirmation emailed." stays up. */
const ADDED_MESSAGE_MS = 10_000;

const FIELD =
  "min-h-11 w-full rounded-md border border-[color:var(--color-line-strong)] bg-[color:var(--color-bg)] px-3.5 text-[16px] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-amber)]";

const HELPER = "text-[14px] font-normal text-[color:var(--color-ink-soft)]";

interface AddedBooking {
  name: string;
  emailed: boolean;
}

/**
 * Manual addition: a place paid by bank transfer, or given at no charge,
 * added by hand. Only for events with a price in the catalogue, so a new
 * paid event can take its first booking before anyone checks out. Folded
 * to one button until someone needs it.
 */
export function ManualAddition({
  eventSlug,
  price,
}: {
  eventSlug: string;
  price: EventPrice;
}) {
  const headingId = useId();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [added, setAdded] = useState<AddedBooking | null>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const shownOpen = useRef(open);
  const tiers = price.tiers;

  // Opening swaps the button for the form, and closing swaps it back, so
  // move focus with it: to the heading (a screen reader then hears what
  // the form is for), and back to the button. Only on a change, so the
  // page never takes focus when it loads.
  useEffect(() => {
    if (shownOpen.current === open) {
      return;
    }
    shownOpen.current = open;
    if (open) {
      headingRef.current?.focus();
    } else {
      openButtonRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!added) {
      return;
    }
    const timer = window.setTimeout(() => setAdded(null), ADDED_MESSAGE_MS);
    return () => window.clearTimeout(timer);
  }, [added]);

  return (
    <div className="mt-8">
      {open ? (
        <section aria-labelledby={headingId} className="surface p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-prose">
              <h3
                id={headingId}
                ref={headingRef}
                tabIndex={-1}
                className="font-display text-title text-[color:var(--color-ink)] outline-none"
              >
                Manual addition
              </h3>
              <p className="mt-1 text-[16px] text-[color:var(--color-ink-soft)]">
                For someone who paid by bank transfer, or who we are giving a
                place to at no charge. Add them here once the money has arrived
                or the gift is agreed.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex min-h-11 shrink-0 items-center rounded-md border border-[color:var(--color-line-strong)] px-5 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
            >
              Cancel
            </button>
          </div>
          <ManualAdditionForm
            eventSlug={eventSlug}
            tiers={tiers}
            currency={price.currency}
            onAdded={(result) => {
              setOpen(false);
              setAdded(result);
              void utils.adminListEventRegistrations.invalidate();
            }}
          />
        </section>
      ) : (
        <button
          ref={openButtonRef}
          type="button"
          onClick={() => {
            setOpen(true);
            setAdded(null);
          }}
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[color:var(--color-line-strong)] px-5 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
        >
          <UserPlus size={18} aria-hidden="true" />
          Add someone by hand
        </button>
      )}

      <p
        role="status"
        className="mt-2 text-[16px] font-medium text-[color:var(--color-success)]"
      >
        {added
          ? `Added ${added.name}. ${
              added.emailed
                ? "Confirmation emailed."
                : "No email given, so nothing was sent."
            }`
          : ""}
      </p>
    </div>
  );
}

/** Adults or children: minus, the number, plus, capped by the place. */
function Stepper({
  label,
  value,
  onChange,
  canAdd,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  canAdd: boolean;
}) {
  const labelId = useId();
  const lower = label.toLowerCase();
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-1.5"
    >
      <span
        id={labelId}
        className="text-[14px] font-semibold text-[color:var(--color-ink)]"
      >
        {label}
      </span>
      <div className="inline-flex w-fit items-center rounded-md border border-[color:var(--color-line-strong)]">
        <button
          type="button"
          aria-label={`Fewer ${lower}`}
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={value === 0}
          className="h-11 w-11 text-[20px] text-[color:var(--color-ink)] hover:text-[color:var(--color-amber)] disabled:opacity-40"
        >
          −
        </button>
        <output
          aria-live="polite"
          className="w-10 text-center text-[16px] font-semibold text-[color:var(--color-ink)] tabular-nums"
        >
          {value}
        </output>
        <button
          type="button"
          aria-label={`More ${lower}`}
          onClick={() => onChange(value + 1)}
          disabled={!canAdd}
          className="h-11 w-11 text-[20px] text-[color:var(--color-ink)] hover:text-[color:var(--color-amber)] disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  );
}

function ManualAdditionForm({
  eventSlug,
  tiers,
  currency,
  onAdded,
}: {
  eventSlug: string;
  /** The types of place this form offers (every type in the catalogue, as the public form sells them). */
  tiers: EventPrice["tiers"];
  currency: string;
  onAdded: (result: AddedBooking) => void;
}) {
  const ids = useId();
  const [tierId, setTierId] = useState(tiers[0]?.id ?? "");
  const [gifted, setGifted] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const addBooking = trpc.adminAddManualBooking.useMutation();
  const utils = trpc.useUtils();
  // Places left per type. A full type can't be picked; the server
  // refuses it too.
  const availability = trpc.eventTierAvailability.useQuery(
    { eventSlug },
    { staleTime: 15_000, refetchOnWindowFocus: true },
  );
  const availabilityById = new Map(
    (availability.data ?? []).map((a) => [a.tierId, a]),
  );
  const tier = tiers.find((t) => t.id === tierId);
  const seats = tier?.seats ?? 1;

  /** Pick a type of place and fit the party inside its seats. */
  function chooseTier(id: string) {
    setTierId(id);
    const nextSeats = tiers.find((t) => t.id === id)?.seats;
    if (nextSeats === undefined) {
      return;
    }
    if (nextSeats <= 1) {
      setAdults(1);
      setChildren(0);
      return;
    }
    if (adults + children > nextSeats) {
      const keptAdults = Math.min(adults, nextSeats);
      setAdults(keptAdults);
      setChildren(Math.min(children, nextSeats - keptAdults));
    }
  }

  // If the chosen type fills (or loads full), move to the first open one.
  useEffect(() => {
    if (!availabilityById.get(tierId)?.atCapacity) {
      return;
    }
    const next = tiers.find((t) => !availabilityById.get(t.id)?.atCapacity);
    if (next) {
      chooseTier(next.id);
    }
    // availabilityById and chooseTier are rebuilt each render;
    // availability.data is the input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability.data, tierId, tiers]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!tierId) {
      setError("Choose the type of place.");
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Please type their name.");
      return;
    }
    if (adults + children < 1) {
      setError("Add at least one person.");
      return;
    }
    addBooking.mutate(
      {
        eventSlug,
        tierId,
        name: trimmedName,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        adults,
        children,
        gifted,
      },
      {
        onSuccess: (res) => {
          void utils.eventTierAvailability.invalidate();
          onAdded({ name: trimmedName, emailed: res.confirmationSent });
        },
        onError: (err) => setError(friendlyError(err)),
      },
    );
  }

  const submitLabel = addBooking.isPending
    ? "Adding…"
    : gifted
      ? "Add gifted place"
      : tier
        ? `Add booking, ${formatMoney(tier.amountCents, currency)} received`
        : "Add booking";

  return (
    <form
      onSubmit={submit}
      className="mt-5 grid gap-5 border-t border-[color:var(--color-line)] pt-5"
    >
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-[14px] font-semibold text-[color:var(--color-ink)]">
          How is this place covered?
        </legend>
        <label className="flex min-h-11 items-center gap-3 text-[16px] text-[color:var(--color-ink)]">
          <input
            type="radio"
            name={`${ids}-covered`}
            checked={!gifted}
            onChange={() => setGifted(false)}
            className="h-5 w-5 accent-[color:var(--color-amber)]"
          />
          Paid by bank transfer
        </label>
        <label className="flex min-h-11 items-center gap-3 text-[16px] text-[color:var(--color-ink)]">
          <input
            type="radio"
            name={`${ids}-covered`}
            checked={gifted}
            onChange={() => setGifted(true)}
            className="h-5 w-5 accent-[color:var(--color-amber)]"
          />
          Gifted, no charge
        </label>
        {gifted ? (
          <p className={HELPER}>
            The booking is recorded with no charge, and the guest gets the
            gifted place email instead of the paid confirmation.
          </p>
        ) : null}
      </fieldset>

      <label className="field-label">
        Type of place
        <select
          value={tierId}
          onChange={(e) => chooseTier(e.target.value)}
          className={FIELD}
        >
          {tiers.map((t) => {
            const a = availabilityById.get(t.id);
            const full = a?.atCapacity === true;
            // Singapore's labels already carry the price ("S$51 per person").
            const priceText = t.label.includes("$")
              ? ""
              : `, ${formatMoney(t.amountCents, currency)}`;
            const size =
              t.seats === 1
                ? ", for 1 person"
                : `, up to ${peopleCount(t.seats)}`;
            const left = full
              ? " (full)"
              : a && a.remaining !== null
                ? ` (${a.remaining} of ${a.capacity} left)`
                : "";
            return (
              <option key={t.id} value={t.id} disabled={full}>
                {`${t.label}${priceText}${size}${left}`}
              </option>
            );
          })}
        </select>
      </label>

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="field-label">
          Full name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            className={FIELD}
          />
        </label>
        <label className="field-label">
          Phone (optional)
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="off"
            className={FIELD}
          />
        </label>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label className="field-label">
            Email (optional)
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              aria-describedby={`${ids}-email-help`}
              className={FIELD}
            />
          </label>
          <p id={`${ids}-email-help`} className={HELPER}>
            We email their confirmation straight away. Without an email, nothing
            is sent.
          </p>
        </div>
      </div>

      {seats <= 1 ? (
        <p className={HELPER}>One person per booking.</p>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-6">
            <Stepper
              label="Adults"
              value={adults}
              onChange={setAdults}
              canAdd={adults + children < seats}
            />
            <Stepper
              label="Children"
              value={children}
              onChange={setChildren}
              canAdd={adults + children < seats}
            />
          </div>
          <p className={HELPER}>This place is for up to {seats} people.</p>
        </div>
      )}

      <p role="alert" className="text-[14px] text-[color:var(--color-danger)]">
        {error ?? ""}
      </p>

      <div>
        <button
          type="submit"
          disabled={addBooking.isPending}
          className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[color:var(--color-amber)] px-5 text-[15px] font-semibold text-[color:var(--color-bg)] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          <Check size={18} aria-hidden="true" />
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
